import {Trans} from '@lingui/macro';
import React, {FC} from 'react';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {PermissionCheck} from '../../../../../../srcCommon/permissions/permissionCheck';
import {PermissionDenied} from '../../../../../../srcCommon/permissions/permissionDenied';
import {useComposeMessageOfflinePermission} from '../../../../../../srcCommon/permissions/useOfflinePermission';
import {PermissionResult} from '../../../../../helpers/permissionHelpers';
import {ChannelModel} from '../../../../../models/api/channelModel';
import {LoadedMessageModel, MessageStatusesEnum} from '../../../../../models/api/messageModel';
import {ReplyLocationsEnum} from '../../../../../services/tracking';
import {MessageViewerHeaderButton} from './messageViewerHeaderButton';

/*
 * Props.
 */

export interface MessageViewerHeaderReplyProps {
  namespace: string;
  message: LoadedMessageModel;
  isUserVisitor: boolean;
  replyChannel?: ChannelModel;
  canReplyAll: boolean;
  onReplyClick: HandlerOf<ReplyLocationsEnum>;
  onReplyAllClick: HandlerOf<ReplyLocationsEnum>;
}

/*
 * Component.
 */

export const MessageViewerHeaderReply: FC<MessageViewerHeaderReplyProps> = (props) => {
  const offlinePermission = useComposeMessageOfflinePermission();

  if (
    !props.replyChannel ||
    props.isUserVisitor ||
    props.message.status === MessageStatusesEnum.DRAFT_COMPOSE
  ) {
    return null;
  }

  const makeOnReplyClickWrapper = (isReplyAll: boolean) =>
    isReplyAll ? props.onReplyAllClick : props.onReplyClick;

  return (
    <PermissionCheck offlinePermission={offlinePermission}>
      {(permission) =>
        renderReplyButton(
          {
            ...props,
            onReplyAllClick: makeOnReplyClickWrapper(true),
            onReplyClick: makeOnReplyClickWrapper(false),
          },
          permission,
        )
      }
    </PermissionCheck>
  );
};

function renderReplyButton(props: MessageViewerHeaderReplyProps, permission: PermissionResult) {
  const {canReplyAll, onReplyAllClick, onReplyClick} = props;
  if (canReplyAll) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <MessageViewerHeaderButton
            iconName="replyAll"
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
            onClick={() => onReplyAllClick(ReplyLocationsEnum.MESSAGE_HEADER)}
            label={<Trans id="message-header-replyall">Reply all</Trans>}
            isDisabled={!permission.isAllowed}
            renderTooltip={() => !permission.isAllowed && <PermissionDenied reason={permission.reason} />}
          />
        )}
      </UserThemeConsumer>
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <MessageViewerHeaderButton
          iconName="reply"
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
          onClick={() => onReplyClick(ReplyLocationsEnum.MESSAGE_HEADER)}
          label={<Trans id="message-header-reply">Reply</Trans>}
          isDisabled={!permission.isAllowed}
          renderTooltip={() => !permission.isAllowed && <PermissionDenied reason={permission.reason} />}
        />
      )}
    </UserThemeConsumer>
  );
}
